
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map, pluck } from 'rxjs/operators';

import { AppStateStorage } from './app-state-storage';

/**
 * Helper containing the names of stores
 */
// eslint-disable-next-line no-shadow
export enum StoreNames {
  CurrentCampaign = 'current_campaign',
  Groupings = 'groupings',
  Queries = 'queries',
  WallChartQueryParams = 'wall_chart_query_params',
  FeatureEnvironment = 'feature_environment',
  UseSkinnyNav = 'use_skinny_nav',
  SuppressMapAccuracyNotice = 'suppress_map_accuracy_notice',
}

/**
 * Extremely basic subscribable store for application state
 * persists in local storage.
 */
@Injectable({
  providedIn: 'root'
})
export class AppPersistantStateService {
  public STORE_NAMES = StoreNames;

  private state = {};
  private subject = new BehaviorSubject<any>(this.state);
  private appStorage = new AppStateStorage();
  /**
   *
   */
  constructor() {
    this.restoreData();
  }

  private restoreData() {
    const currentCampaign = this.appStorage.read<any>(StoreNames.CurrentCampaign);
    const groupings = this.appStorage.read<any>(StoreNames.Groupings);
    const queries = this.appStorage.read<any>(StoreNames.Queries);
    const featureEnvironment = this.appStorage.read<any>(StoreNames.FeatureEnvironment);
    const useSkinnyNav = this.appStorage.read<any>(StoreNames.UseSkinnyNav);
    const wallChartQueryParams = this.appStorage.read<any>(StoreNames.WallChartQueryParams);
    const suppressMapAccuracyNotice = this.appStorage.read<any>(StoreNames.SuppressMapAccuracyNotice);


    this.update(StoreNames.CurrentCampaign, currentCampaign);
    this.update(StoreNames.WallChartQueryParams, wallChartQueryParams);
    this.update(StoreNames.Groupings, groupings);
    this.update(StoreNames.Queries, queries);
    this.update(StoreNames.FeatureEnvironment, featureEnvironment);
    this.update(StoreNames.UseSkinnyNav, useSkinnyNav);
    this.update(StoreNames.SuppressMapAccuracyNotice, suppressMapAccuracyNotice);
  }

  clear(...stores: StoreNames[]) {
    stores.forEach(store => {
      this.update(store, undefined);
    });
  }

  /**
   * Update the data in a store
   *
   * @param store the name of the store
   * @param data the data to set the store value to
   */
  update<T>(store: StoreNames, data: T) {
    this.state[store] = data;
    this.appStorage.write(store, data);
    this.subject.next(this.state);
  }

  getState() {
    return cloneDeep(this.state);
  }

  /**
   * Listen for changes to a store
   *
   * @param store
   */
  get<T>(store: string) {
    return this.subject.pipe(
      map(r => r[store]),
      distinctUntilChanged()) as Observable<T>;
  }

  /**
   * Get a copy of the raw value of the store
   *
   * @param store
   */
  getValue<T>(store: string): T {
    return cloneDeep(this.state[store]) as T;
  }
}
